import {buildCommentEditBatch,commentEditPayload} from './comment-edit-batch';
import { CreativeEditPanel } from './CreativeEditPanel';
import { useCreativeEditStore } from './creative-edit-store';
import { useCreativeLayersStore } from './creative-layers-store';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useCanvasStore, registerEditFixDispatch } from './canvas-store';
import { useActiveProjectImages, useActiveProjectId } from './store-selectors';
import {
  Undo2,
  Redo2,
  ChevronDown,
  PenLine,
  Loader2,
  Download,
  Plus,
  MessageSquarePlus,
  Eye,
} from 'lucide-react';
import type { UndoAction } from './canvas-types';
import { downloadCreatives } from './download-creatives';
import { StyleVariationsPanel } from './style-variations-panel';
import { CreateVariationsPanel } from './create-variations-panel';
import { CopySidebarPanel } from './CopySidebarPanel';
import { PreviewSidebarPanel } from './PreviewSidebarPanel';
import type { AdCopyVariation } from './canvas-store';

const ASPECT_RATIO_OPTIONS = [
  { value: '1:1',  label: '1:1',  desc: 'Square' },
  { value: '9:16', label: '9:16', desc: 'Vertical' },
  { value: '16:9', label: '16:9', desc: 'Landscape' },
  { value: '4:5',  label: '4:5',  desc: 'Instagram Feed' },
  { value: '3:4',  label: '3:4',  desc: 'Pinterest' },
];

function undoActionLabel(action: UndoAction): string {
  switch (action.type) {
    case 'MOVE': return 'Move';
    case 'RESIZE': return 'Resize';
    case 'DELETE': return 'Delete';
    case 'ADD': return 'Add';
    case 'DUPLICATE': return 'Duplicate';
    case 'ADD_COMMENT': return 'Add comment';
    case 'DELETE_COMMENT': return 'Delete comment';
    case 'APPLY_COMMENTS': return 'Apply comments';
  }
}

function AdCopyPopover({
  selectedImageIds,
  projectId,
  onClose,
  onGenerate,
}: {
  selectedImageIds: string[];
  projectId: number | null;
  onClose: () => void;
  onGenerate: (variationsPerAd: number, additionalDirection: string) => void;
}) {
  const [variationsPerAd, setVariationsPerAd] = useState(5);
  const [additionalDirection, setAdditionalDirection] = useState('');

  return (
    <div
      data-testid="ad-copy-popover"
      style={{
        position: 'absolute',
        bottom: '110%',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#FFFFFF',
        border: '1px solid #E8E5E0',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        width: 280,
        zIndex: 300,
        padding: '16px',
        color: '#3D3A36',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <PenLine size={14} style={{ color: '#6366F1' }} />
        Generate Ad Copy
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#8C8578', display: 'block', marginBottom: 6 }}>
          {selectedImageIds.length} image{selectedImageIds.length !== 1 ? 's' : ''} selected
        </label>
        <div style={{ fontSize: 11, color: '#8C8578' }}>
          Variations per ad
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <input
            data-testid="copy-variations-count"
            type="number"
            min={1}
            max={10}
            value={variationsPerAd}
            onChange={(e) => setVariationsPerAd(Math.min(10, Math.max(1, Number(e.target.value))))}
            style={{
              width: 64, textAlign: 'center', fontWeight: 700, fontSize: 14,
              background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.35)',
              borderRadius: 6, padding: '4px 8px', color: '#6366F1',
              outline: 'none',
            }}
          />
          <span style={{ fontSize: 11, color: '#8C8578' }}>variations per ad</span>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#8C8578', display: 'block', marginBottom: 6 }}>
          Additional direction (optional)
        </label>
        <textarea
          data-testid="copy-additional-direction"
          value={additionalDirection}
          onChange={(e) => setAdditionalDirection(e.target.value)}
          placeholder="e.g. Focus on the summer sale, use emojis, target fitness beginners..."
          rows={3}
          style={{
            width: '100%',
            background: '#F7F6F3',
            border: '1px solid #E5E2DC',
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 12,
            color: '#3D3A36',
            resize: 'none',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          data-testid="copy-cancel-btn"
          onClick={onClose}
          style={{
            flex: 1,
            padding: '8px',
            borderRadius: 8,
            border: '1px solid #E5E2DC',
            background: 'transparent',
            color: '#8C8578',
            fontSize: 12,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Cancel
        </button>
        <button
          data-testid="copy-generate-btn"
          onClick={() => onGenerate(variationsPerAd, additionalDirection)}
          style={{
            flex: 2,
            padding: '8px',
            borderRadius: 8,
            border: 'none',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: '#FFFFFF',
            fontSize: 12,
            cursor: 'pointer',
            fontWeight: 700,
          }}
        >
          Generate Copy
        </button>
      </div>
    </div>
  );
}

export function CanvasToolbar() {
  const {
    undo,
    redo,
    undoStack,
    redoStack,
    selectedImageIds,
    selectedAspectRatio,
    setSelectedAspectRatio,
    appendCanvasAdCopyVariation,
    setShowCopySidebar,
    showCopySidebar,
    showPreviewSidebar,
    setShowPreviewSidebar,
    setAdCopyGenerating,
    addImageToCanvas,
    commentMode,
    setCommentMode,
  } = useCanvasStore();
  const activeEditImageId = useCreativeEditStore(s => s.activeImageId);
  const editIntent = useCreativeEditStore(s => s.editIntent);
  const prepares = useCreativeEditStore(s => s.prepares);
  const activeLayerImageId = useCreativeLayersStore(s => s.activeImageId);
  const comments = useCanvasStore((s) => s.comments);
  const sendAgentMessage = useCanvasStore((s) => s.sendAgentMessage);
  const setCommentPopover = useCanvasStore((s) => s.setCommentPopover);
  const images = useActiveProjectImages();
  const persistenceProjectId = useActiveProjectId();
  const projectComments = comments.filter(comment => images.some(image => image.id === comment.imageId));
  const freshCommentCount = projectComments.filter(comment => comment.isPending && !comment.isFailed).length;
  const failedCommentCount = projectComments.filter(comment => comment.isPending && comment.isFailed).length;
  const retryComments = freshCommentCount === 0;
  const pendingCommentCount = retryComments ? failedCommentCount : freshCommentCount;

  const selectedEditImageId = selectedImageIds.length === 1 && images.some(i => i.id === selectedImageIds[0]) ? selectedImageIds[0] : null;
  useEffect(() => {
    useCreativeEditStore.getState().focusSelection(selectedEditImageId, !!activeLayerImageId);
  }, [selectedEditImageId, persistenceProjectId, editIntent, prepares, activeLayerImageId]);

  const [showStylesPanel, setShowStylesPanel] = useState(false);
  const [showVariationsPanel, setShowVariationsPanel] = useState(false);

  // Auto-open the Create Variations panel when something queued an automatic
  // clone (e.g. the Ad Library "Clone" action drops a template on canvas and
  // wants 2 variations of it). The panel itself reads the same store flag
  // and auto-fires generate on mount.
  const pendingAutoVariations = useCanvasStore((s) => s.pendingAutoVariations);
  useEffect(() => {
    if (!pendingAutoVariations) return;
    if (!useCanvasStore.getState().selectedImageIds.includes(pendingAutoVariations.imageId)) return;
    setShowVariationsPanel(true);
    setShowStylesPanel(false);
  }, [pendingAutoVariations]);
  const [showAdCopyPopover, setShowAdCopyPopover] = useState(false);
  const [isGeneratingCopy, setIsGeneratingCopy] = useState(false);
  const [ratioOpen, setRatioOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const ratioRef = useRef<HTMLDivElement>(null);
  const adCopyRef = useRef<HTMLDivElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Upload handler: POSTs files to /api/upload, then adds each to the canvas
  const handleUploadFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        try {
          const res = await fetch('/api/upload', { method: 'POST', body: formData });
          if (!res.ok) {
            console.error('[upload] upload failed:', res.status);
            continue;
          }
          const { url } = await res.json() as { url: string };

          // Load image to get natural dimensions
          const dims = await new Promise<{ w: number; h: number }>((resolve) => {
            const img = new window.Image();
            img.onload = () => resolve({ w: img.naturalWidth || 1080, h: img.naturalHeight || 1080 });
            img.onerror = () => resolve({ w: 1080, h: 1080 });
            img.src = url;
          });

          // Add to canvas using the existing addImageToCanvas action
          await addImageToCanvas(
            url,
            dims.w,
            dims.h,
            file.name,
            dims.w,
            dims.h,
            'User upload'
          );
        } catch (err) {
          console.error('[upload] error uploading file:', err);
        }
      }
    } finally {
      setIsUploading(false);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  }, [addImageToCanvas]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ratioRef.current && !ratioRef.current.contains(e.target as Node)) {
        setRatioOpen(false);
      }
    };
    if (ratioOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ratioOpen]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (adCopyRef.current && !adCopyRef.current.contains(e.target as Node)) {
        setShowAdCopyPopover(false);
      }
    };
    if (showAdCopyPopover) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showAdCopyPopover]);

  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;
  const hasSelection = selectedImageIds.length > 0;

  const selectedImages = images.filter((img) => selectedImageIds.includes(img.id));

  // v4.52 — server-built download: bare image for a single copyless ad,
  // otherwise a zip of numbered images + ads.csv (Meta-mapped copy columns).
  // The server fetches the CDN images itself, so the browser CORS wall that
  // made downloads open fullscreen in a tab is gone for good.
  const handleDownload = useCallback(async () => {
    if (selectedImages.length === 0 || !persistenceProjectId) return;
    const result = await downloadCreatives(persistenceProjectId, selectedImages.map((img) => img.id));
    if (!result.ok) {
      console.warn('[canvas-toolbar] download failed:', result.error);
    }
  }, [selectedImages, persistenceProjectId]);

  const handleGenerateCopy = useCallback(async (variationsPerAd: number, additionalDirection: string) => {
    if (!persistenceProjectId || selectedImageIds.length === 0) return;
    setShowAdCopyPopover(false);
    setIsGeneratingCopy(true);
    setShowCopySidebar(true);
    setAdCopyGenerating(selectedImageIds, true);

    try {
      const response = await fetch('/api/canvas/generate-ad-copy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: persistenceProjectId,
          imageClientIds: selectedImageIds,
          variationsPerAd,
          additionalDirection,
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error('Failed to start ad copy generation');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          const lines = part.split('\n');
          let eventType = '';
          let dataStr = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim();
            if (line.startsWith('data: ')) dataStr = line.slice(6).trim();
          }
          if (!eventType || !dataStr) continue;

          try {
            const data = JSON.parse(dataStr);
            if (eventType === 'copy_complete' && data.variation) {
              appendCanvasAdCopyVariation(data.imageClientId, data.variation as AdCopyVariation);
            }
          } catch {
          }
        }
      }
    } catch (err) {
      console.error('[AdCopyGeneration] Error:', err);
    } finally {
      setIsGeneratingCopy(false);
      setAdCopyGenerating(selectedImageIds, false);
    }
  }, [persistenceProjectId, selectedImageIds, appendCanvasAdCopyVariation, setShowCopySidebar, setAdCopyGenerating]);

  const toolBtn = (
    active: boolean,
    disabled: boolean,
    icon: React.ReactNode,
    label: string,
    testId: string,
    onClick: () => void
  ) => (
    <button
      data-testid={testId}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={label}
      className={[
        'flex items-center justify-center rounded-full transition-all',
        'w-9 h-9',
        disabled
          ? 'text-gray-600 opacity-50 cursor-not-allowed'
          : active
          ? 'bg-indigo-500/20 ring-1 ring-indigo-400/40 text-white'
          : 'text-gray-400 hover:text-white hover:bg-white/10',
      ].join(' ')}
    >
      {icon}
    </button>
  );

  const selectedImageId = selectedImageIds[0];
  if (activeEditImageId && activeEditImageId === selectedEditImageId) return <CreativeEditPanel projectId={persistenceProjectId} />;

  return (
    <>
      <div
        data-testid="canvas-toolbar"
        className="flex items-center gap-1 px-3 py-2 rounded-full shadow-lg"
        style={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          // Bumped from 200 → 600 so the toolbar's popovers (Style
          // Variations, Create Variations, Aspect Ratio) render ABOVE the
          // chat sidebar (which uses zIndex 500 for its overlay). Without
          // this, the popovers opened from the toolbar were getting
          // clipped/hidden behind the chat panel.
          zIndex: 600,
          background: 'rgba(17,24,39,0.9)',
          backdropFilter: 'blur(8px)',
          pointerEvents: 'all',
        }}
      >
        {/* Styles button (LayoutGrid) — opens Style Variations panel */}
        <div style={{ position: 'relative' }}>
          {showStylesPanel && hasSelection && selectedImageId && (
            <StyleVariationsPanel
              selectedImageId={selectedImageId}
              onClose={() => setShowStylesPanel(false)}
            />
          )}
        </div>

        {/* Variations button (RefreshCw) — opens Create Variations panel */}
        <div style={{ position: 'relative' }}>
          {showVariationsPanel && hasSelection && selectedImageId && (
            <CreateVariationsPanel
              selectedImageId={selectedImageId}
              onClose={() => setShowVariationsPanel(false)}
            />
          )}
        </div>

        {/* Comment button — toggles "click any image to add an edit comment"
            mode. When on, clicks on canvas images open the comment popover
            pinned at the click point. Once at least one comment exists, the
            Apply button below appears in the toolbar and lets the user fire
            run_variations_v3 with the bundled per-image comments as
            creativeDirection. */}
        <div style={{ position: 'relative' }}>
          {toolBtn(
            commentMode,
            false, // always usable — comment mode is a global toggle
            <MessageSquarePlus className="w-4 h-4" />,
            commentMode ? 'Comment mode — click any image to comment (click again to exit)' : 'Comment on images to apply edits',
            'canvas-comment-mode',
            () => {
              setCommentMode(!commentMode);
              setShowVariationsPanel(false);
              setShowStylesPanel(false);
              setShowAdCopyPopover(false);
            }
          )}
        </div>

        {/* Brush button removed (v3.87, user request) — the brush/inpaint tool
            is parked for now. canvas-brush-mode.tsx stays for when it returns. */}

        {/* Divider */}
        <div className="w-px h-5 bg-white/15 mx-1" />

        {/* Aspect ratio dropdown */}
        <div ref={ratioRef} style={{ position: 'relative' }}>
          <button
            data-testid="canvas-aspect-ratio-btn"
            onClick={() => setRatioOpen((v) => !v)}
            title="Aspect ratio"
            className={[
              'flex items-center gap-1 rounded-full px-2.5 h-9 text-xs font-semibold transition-all',
              ratioOpen
                ? 'bg-indigo-500/20 ring-1 ring-indigo-400/40 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/10',
            ].join(' ')}
          >
            {selectedAspectRatio}
            <ChevronDown className="w-3 h-3 opacity-60" />
          </button>
          {ratioOpen && (
            <div
              style={{
                position: 'absolute',
                bottom: '110%',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#FFFFFF',
                border: '1px solid #E8E5E0',
                borderRadius: 10,
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                minWidth: 180,
                zIndex: 100,
                overflow: 'hidden',
              }}
            >
              {ASPECT_RATIO_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  data-testid={`ratio-option-${opt.value.replace(':', '-')}`}
                  onClick={() => { setSelectedAspectRatio(opt.value); setRatioOpen(false); }}
                  className="flex items-center gap-2.5 w-full transition-colors"
                  style={{
                    padding: '9px 14px',
                    fontSize: 12,
                    color: selectedAspectRatio === opt.value ? '#FFFFFF' : '#9CA3AF',
                    background: selectedAspectRatio === opt.value ? 'rgba(99,102,241,0.2)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.07)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = selectedAspectRatio === opt.value ? 'rgba(99,102,241,0.2)' : 'transparent'; }}
                >
                  <span style={{ fontWeight: 700, marginRight: 4, color: selectedAspectRatio === opt.value ? '#FFFFFF' : '#E5E7EB' }}>{opt.label}</span>
                  <span style={{ fontSize: 11, color: '#8C8578' }}>{opt.desc}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Download button */}
        {hasSelection && toolBtn(
          false,
          false,
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Download className="w-4 h-4" />
            {selectedImageIds.length >= 2 && (
              <span style={{
                position: 'absolute',
                top: -6,
                right: -7,
                background: '#6366f1',
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                borderRadius: '50%',
                width: 14,
                height: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                pointerEvents: 'none',
              }}>
                {selectedImageIds.length}
              </span>
            )}
          </div>,
          selectedImageIds.length === 1 ? 'Download image as PNG' : `Download ${selectedImageIds.length} images as ZIP`,
          'canvas-download-btn',
          handleDownload
        )}

        {/* Preview button — native Facebook post previews of the selected ads
            (or every ad when nothing is selected) in a right slide-out. */}
        {toolBtn(
          showPreviewSidebar,
          images.length === 0,
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Eye className="w-4 h-4" />
            {selectedImageIds.length >= 2 && (
              <span style={{
                position: 'absolute',
                top: -6,
                right: -7,
                background: '#6366f1',
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                borderRadius: '50%',
                width: 14,
                height: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                pointerEvents: 'none',
              }}>
                {selectedImageIds.length}
              </span>
            )}
          </div>,
          selectedImageIds.length > 0
            ? `Preview ${selectedImageIds.length} selected as Facebook posts`
            : 'Preview all ads as Facebook posts',
          'canvas-preview-btn',
          () => setShowPreviewSidebar(!showPreviewSidebar)
        )}

        {/* Divider */}
        <div className="w-px h-5 bg-white/15 mx-1" />

        {/* Upload image button */}
        <button
          data-testid="canvas-upload-btn"
          onClick={() => uploadInputRef.current?.click()}
          disabled={isUploading}
          title={isUploading ? 'Uploading…' : 'Upload image to canvas'}
          className="flex items-center justify-center w-9 h-9 rounded-full text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </button>
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleUploadFiles(e.target.files)}
        />

        {/* Apply Comments button — slides out from the right of the upload
            button when there are pending comments. v2.26: sends ONE forced
            run_edit_fixes message (same path as the Apply bar) — the edit
            playbook executes directly, no LLM routing. Previously this sent
            free text and hoped the agent picked run_variations_v3; observed
            Jun 12 it chatted instead ("Would you like me to...") and then
            hallucinated completion on "yes" with zero tool calls. Comments
            are tracked by exact dispatch snapshots; failed results reopen
            only the sent, unchanged pins for Retry. */}
        {pendingCommentCount > 0 && (
          <button
            data-testid="canvas-apply-comments"
            onClick={() => {
              if (persistenceProjectId == null) return;
              const {items,invalidCommentIds} = buildCommentEditBatch(images, projectComments, [], retryComments);
              const store = useCanvasStore.getState();
              if (invalidCommentIds.length) store.markCommentsFailedByIds(invalidCommentIds);
              setCommentPopover(null);
              if (!items.length) return;
              registerEditFixDispatch(items, persistenceProjectId);
              store.applyPendingComments(items.flatMap(item => item.commentIds));
              const totalEdits = items.reduce((s, i) => s + i.comments.length, 0);
              const message = `Apply ${totalEdits} edit${totalEdits === 1 ? '' : 's'} to ${items.length} image${items.length === 1 ? '' : 's'}`;
              sendAgentMessage(message, undefined, {
                forcedTool: { name: 'run_edit_fixes', arguments: { items: items.map(commentEditPayload) } },
              }).catch((err) => {
                console.error("[canvas-toolbar] apply comments failed:", err);
              });
            }}
            title={`${retryComments ? 'Retry' : 'Apply'} ${pendingCommentCount} pending comment${pendingCommentCount === 1 ? "" : "s"} — regenerate the commented ad${pendingCommentCount === 1 ? "" : "s"} with your edits`}
            style={{
              // Slide-out animation: starts off-screen left of its slot with
              // zero width + opacity, animates to full size beside the
              // upload button. Plays only when the button first mounts
              // (i.e. when pendingCommentCount transitions 0 → >0).
              animation: 'apply-slide-in 220ms cubic-bezier(0.4, 0, 0.2, 1) both',
              transformOrigin: 'left center',
              marginLeft: 4,
              height: 28,
              padding: '0 10px',
              borderRadius: 999,
              border: '1px solid rgba(255,255,255,0.85)',
              background: '#ffffff',
              color: '#1f2937',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            }}
          >
            {retryComments ? 'Retry' : 'Apply'} ({pendingCommentCount})
            <style>{`
              @keyframes apply-slide-in {
                from {
                  opacity: 0;
                  transform: translateX(-6px) scaleX(0.85);
                  filter: blur(2px);
                }
                to {
                  opacity: 1;
                  transform: translateX(0) scaleX(1);
                  filter: blur(0);
                }
              }
            `}</style>
          </button>
        )}
      </div>

      {/* Copy Sidebar Panel - rendered outside toolbar for full-height positioning */}
      <CopySidebarPanel
        open={showCopySidebar}
        onClose={() => setShowCopySidebar(false)}
        projectId={persistenceProjectId}
      />

      {/* Facebook Preview slide-out — same full-height pattern */}
      <PreviewSidebarPanel
        open={showPreviewSidebar}
        onClose={() => setShowPreviewSidebar(false)}
        projectId={persistenceProjectId}
      />
    </>
  );
}
